import React, { useRef, useEffect, useState, useCallback } from "react";
import LoaderLulo from "../../../../components/loaderLulo";
import Loader from "../../../../components/loader";
import "./index.css";
import { useLocation } from "react-router-dom";
import { documentValidator } from "../../../../services/documentValidator";
import { forceFocus, optimizeImageQuality } from "../../utils";

const ZONA_CONFORT_MOVIL = {
  x: 0.10,
  y: 0.15,
  width: 0.80,
  height: 0.68
};

const ZONA_CONFORT_DESKTOP = {
  x: 0.20,
  y: 0.12,
  width: 0.60,
  height: 0.76
};

const UMBRALES = {
  luminosidadMin: 35,
  luminosidadMax: 235,
  ocupacionEmpty: 0.04,
  ocupacionFar: 0.12,
  ocupacionNear: 0.55,
  toleranciaLuminancia: 40
};

const MENSAJES = {
  empty: "No se detecta documento",
  far: "Acerca el documento",
  near: "Aléjalo un poco",
  descentrado: "Centra el documento",
  rotado: "Gira el documento",
  ok: "Sostén el documento"
};

const THROTTLE_ENVIO = 500; // Optimizado: Envío cada 500ms para mejor validación
const FRAMES_NECESARIOS = 3;
const MAX_WIDTH = 720;
const CALIDAD_WEBP = 0.75;

export function WrapperCardCaptureV8() {
  const location = useLocation();
  const [config, setConfig] = useState({});
  const [configLoading, setLoading] = useState(true);

  const params = new Proxy(new URLSearchParams(window.location.search), {
    get: (searchParams, prop) => searchParams.get(prop),
  });

  const projectName = params.projectName || "Default";

  const url = () => {
    if (location.pathname.includes("card-capture")) {
      return `https://lnesv3-conf.ado-tech.com/${projectName}/CardCapture/config.json`;
    }
    return `https://lnesv3-conf.ado-tech.com/${projectName}/Liveness/config.json`;
  };

  const defaultUrl = () => {
    if (location.pathname.includes("card-capture")) {
      return `https://lnesv3-conf.ado-tech.com/Default/CardCapture/config.json`;
    }
    return `https://lnesv3-conf.ado-tech.com/Default/Liveness/config.json`;
  };

  function setStyles(stylesObject) {
    const root = document.documentElement;
    root.style.setProperty("--background-color", stylesObject.backgroundColor);
    root.style.setProperty("--message-color", stylesObject.messageColor);
    root.style.setProperty("--message-back-color", stylesObject.messageBackgroundColor);
    root.style.setProperty("--guide-color", stylesObject.guideColor);
    root.style.setProperty("--success-color", stylesObject.successColor);
    root.style.setProperty("--error-color", stylesObject.errorColor);
    root.style.setProperty("--message-error-color", stylesObject.messageErrorColor);
    root.style.setProperty("--font-size", stylesObject.fontSize);
  }

  const fetchConfig = async () => {
    try {
      console.log(`[CardCaptureV8] Intentando cargar config desde: ${url()}`);
      const response = await fetch(url(), { method: "GET" });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log("[CardCaptureV8] Config cargada exitosamente:", data);
      setConfig(data);
      setStyles(data.styles);
    } catch (error) {
      console.warn(`[CardCaptureV8] Error al cargar config principal: ${error.message}`);
      console.log(`[CardCaptureV8] Intentando cargar config por defecto desde: ${defaultUrl()}`);
      try {
        const response = await fetch(defaultUrl(), { method: "GET" });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        console.log("[CardCaptureV8] Config por defecto cargada exitosamente");
        setConfig(data);
        setStyles(data.styles);
      } catch (fallbackError) {
        console.error("[CardCaptureV8] Error al cargar config por defecto:", fallbackError);
        alert(`Error al cargar configuración: No se pudo acceder ni a la configuración del proyecto "${projectName}" ni a la configuración por defecto. Por favor verifica la conectividad.`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  if (configLoading) {
    return null;
  }

  return <CardCaptureV8 configLoading={configLoading} configValue={config} />;
}

function obtenerZona(ctx, width, height, zona) {
  const x = Math.floor(width * zona.x);
  const y = Math.floor(height * zona.y);
  const w = Math.floor(width * zona.width);
  const h = Math.floor(height * zona.height);
  const imageData = ctx.getImageData(x, y, w, h);
  return imageData;
}

function luminanciaPromedio(imageData) {
  const data = imageData.data;
  let suma = 0;
  const pixeles = data.length / 4;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const lum = 0.299 * r + 0.587 * g + 0.114 * b;
    suma += lum;
  }
  return suma / pixeles;
}

function ocupacion(imageData, mediaLum, tolerancia) {
  const data = imageData.data;
  const pixeles = data.length / 4;
  let diferentes = 0;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const lum = 0.299 * r + 0.587 * g + 0.114 * b;
    if (Math.abs(lum - mediaLum) > tolerancia) {
      diferentes++;
    }
  }
  return diferentes / pixeles;
}

function detectarBordes(imageData) {
  const data = imageData.data;
  const width = imageData.width;
  const height = imageData.height;
  let bordesDetectados = 0;
  const umbralBorde = 30;

  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = (y * width + x) * 4;
      const idxAbajo = ((y + 1) * width + x) * 4;
      const idxDerecha = (y * width + (x + 1)) * 4;

      const lum = 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];
      const lumAbajo = 0.299 * data[idxAbajo] + 0.587 * data[idxAbajo + 1] + 0.114 * data[idxAbajo + 2];
      const lumDerecha = 0.299 * data[idxDerecha] + 0.587 * data[idxDerecha + 1] + 0.114 * data[idxDerecha + 2];

      if (Math.abs(lum - lumAbajo) > umbralBorde || Math.abs(lum - lumDerecha) > umbralBorde) {
        bordesDetectados++;
      }
    }
  }

  const totalPixeles = (width - 2) * (height - 2);
  return bordesDetectados / totalPixeles;
}

function detectarEsquinas(imageData) {
  const data = imageData.data;
  const width = imageData.width;
  const height = imageData.height;
  const umbralEsquina = 50;
  const esquinas = [];

  for (let y = 2; y < height - 2; y++) {
    for (let x = 2; x < width - 2; x++) {
      const idx = (y * width + x) * 4;

      const lum = 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];

      let gradienteH = 0;
      let gradienteV = 0;

      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const idxVecino = ((y + dy) * width + (x + dx)) * 4;
          const lumVecino = 0.299 * data[idxVecino] + 0.587 * data[idxVecino + 1] + 0.114 * data[idxVecino + 2];
          gradienteH += dx * (lumVecino - lum);
          gradienteV += dy * (lumVecino - lum);
        }
      }

      const magnitud = Math.sqrt(gradienteH * gradienteH + gradienteV * gradienteV);

      if (magnitud > umbralEsquina) {
        esquinas.push({ x, y, magnitud });
      }
    }
  }

  esquinas.sort((a, b) => b.magnitud - a.magnitud);

  if (esquinas.length < 4) return null;

  const cuatroEsquinas = [esquinas[0]];
  const distanciaMinima = Math.min(width, height) * 0.2;

  for (let i = 1; i < esquinas.length && cuatroEsquinas.length < 4; i++) {
    const esquina = esquinas[i];
    let lejos = true;

    for (const e of cuatroEsquinas) {
      const dist = Math.sqrt(Math.pow(esquina.x - e.x, 2) + Math.pow(esquina.y - e.y, 2));
      if (dist < distanciaMinima) {
        lejos = false;
        break;
      }
    }

    if (lejos) {
      cuatroEsquinas.push(esquina);
    }
  }

  return cuatroEsquinas.length === 4 ? cuatroEsquinas : null;
}

function calcularCentroDocumento(esquinas) {
  if (!esquinas || esquinas.length !== 4) return null;

  const centroX = esquinas.reduce((sum, e) => sum + e.x, 0) / 4;
  const centroY = esquinas.reduce((sum, e) => sum + e.y, 0) / 4;

  return { x: centroX, y: centroY };
}

function calcularRotacion(esquinas) {
  if (!esquinas || esquinas.length !== 4) return 0;

  esquinas.sort((a, b) => a.y - b.y);
  const superior = esquinas.slice(0, 2);
  superior.sort((a, b) => a.x - b.x);

  const esquinaSupIzq = superior[0];
  const esquinaSupDer = superior[1];

  const dx = esquinaSupDer.x - esquinaSupIzq.x;
  const dy = esquinaSupDer.y - esquinaSupIzq.y;

  const angulo = Math.atan2(dy, dx) * (180 / Math.PI);

  return Math.abs(angulo);
}

function estaDescentrado(centroDoc, width, height) {
  if (!centroDoc) return true;

  const centroZonaX = width / 2;
  const centroZonaY = height / 2;

  const desviacionX = Math.abs(centroDoc.x - centroZonaX) / width;
  const desviacionY = Math.abs(centroDoc.y - centroZonaY) / height;

  const umbralDescentrado = 0.15;

  return desviacionX > umbralDescentrado || desviacionY > umbralDescentrado;
}

function clasificarDistancia(ctx, width, height, zonaConfort) {
  const zona = obtenerZona(ctx, width, height, zonaConfort);
  const lum = luminanciaPromedio(zona);

  if (lum < UMBRALES.luminosidadMin || lum > UMBRALES.luminosidadMax) {
    return "empty";
  }

  const ocup = ocupacion(zona, lum, UMBRALES.toleranciaLuminancia);
  const bordes = detectarBordes(zona);

  if (ocup < UMBRALES.ocupacionEmpty || bordes < 0.02) {
    return "empty";
  }

  const esquinas = detectarEsquinas(zona);

  if (!esquinas || esquinas.length < 4) {
    if (ocup < UMBRALES.ocupacionFar || bordes < 0.05) {
      return "far";
    }
    return "empty";
  }

  const centroDoc = calcularCentroDocumento(esquinas);
  const rotacion = calcularRotacion(esquinas);
  const descentrado = estaDescentrado(centroDoc, zona.width, zona.height);

  const umbralRotacion = 15;

  if (ocup < UMBRALES.ocupacionFar) {
    return "far";
  }

  if (ocup > UMBRALES.ocupacionNear) {
    return "near";
  }

  if (rotacion > umbralRotacion) {
    return "rotado";
  }

  if (descentrado) {
    return "descentrado";
  }

  return "ok";
}

export default function CardCaptureV8({ configValue, configLoading }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const containerAdo = useRef(null);
  const animationFrameId = useRef(null);
  const ultimoEnvio = useRef(0);
  const procesandoEnvio = useRef(false);

  const [loading, setLoading] = useState(true);
  const [mensaje, setMensaje] = useState(MENSAJES.empty);
  const [estadoActual, setEstadoActual] = useState("empty");
  const [framesBuenos, setFramesBuenos] = useState([]);
  const [availableCameras, setAvailableCameras] = useState([]);
  const [currentCameraIndex, setCurrentCameraIndex] = useState(0);
  const [isChangingCamera, setIsChangingCamera] = useState(false);
  const [showFocusIndicator, setShowFocusIndicator] = useState(false);
  const [focusPosition, setFocusPosition] = useState({ x: 0, y: 0 });
  const torchSupported = useRef(false);
  const [torchEnabled, setTorchEnabled] = useState(false);
  const [mostrarComponentes, setMostrarComponentes] = useState(true);
  const [isMirrored, setIsMirrored] = useState(false);
  const [debugInfo, setDebugInfo] = useState({
    lum: 0,
    ocup: 0,
    bordes: 0,
    esquinas: 0,
    rotacion: 0,
    descentrado: false,
    centroX: 0,
    centroY: 0
  });

  const params = new Proxy(new URLSearchParams(window.location.search), {
    get: (searchParams, prop) => searchParams.get(prop),
  });

  const projectName = params.projectName || "Default";
  const processId = params.processId;
  const documentType = params.documentType;
  const isFront = params.isFront || "true";
  const platform = /iPad|iPhone|iPod/.test(navigator.userAgent) ? "ios" : "android";
  const isReactNative = typeof window !== 'undefined' && window.ReactNativeWebView !== undefined;
  const isMovilDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const debugMode = params.debug === "true";

  const getZonaConfort = () => {
    return isMovilDevice ? ZONA_CONFORT_MOVIL : ZONA_CONFORT_DESKTOP;
  };

  useEffect(() => {
    const getCameras = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const cameras = devices.filter((device) => device.kind === "videoinput");
        const backCameras = cameras.filter((camera) =>
          camera.label.toLowerCase().includes("back") ||
          camera.label.toLowerCase().includes("rear") ||
          camera.label.toLowerCase().includes("trasera")
        );
        setAvailableCameras(backCameras.length > 0 ? backCameras : cameras);
      } catch (error) {
      }
    };
    getCameras();
  }, []);

  const iniciarCamara = async (deviceId = null) => {
    try {
      const constraints = {
        video: deviceId
          ? {
              deviceId: { exact: deviceId },
              width: { ideal: 1920 },
              height: { ideal: 1080 },
              frameRate: { ideal: 30 },
              focusMode: { ideal: "continuous" },
              exposureMode: { ideal: "continuous" },
              whiteBalanceMode: { ideal: "continuous" }
            }
          : {
              facingMode: "environment",
              width: { ideal: 1920 },
              height: { ideal: 1080 },
              frameRate: { ideal: 30 },
              focusMode: { ideal: "continuous" },
              exposureMode: { ideal: "continuous" },
              whiteBalanceMode: { ideal: "continuous" }
            }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();

        const track = stream.getVideoTracks()[0];
        const capabilities = track.getCapabilities ? track.getCapabilities() : {};
        const settings = track.getSettings();

        // Detectar si es cámara frontal para aplicar mirror mode
        const isFrontCamera = settings.facingMode === "user";
        setIsMirrored(isFrontCamera);

        if (capabilities.torch) {
          torchSupported.current = true;
        }

        if (settings.focusMode === "manual" || !settings.focusMode) {
          try {
            await track.applyConstraints({
              advanced: [{ focusMode: "continuous" }]
            });
          } catch (e) {}
        }

        setLoading(false);
        setMostrarComponentes(true);
        iniciarAnalisis();
      }
    } catch (error) {
      setLoading(false);
      setMostrarComponentes(true);
      alert("Error al acceder a la cámara: " + error.message);
    }
  };

  useEffect(() => {
    if (availableCameras.length > 0 && !configLoading) {
      iniciarCamara(availableCameras[0].deviceId);
    }

    return () => {
      if (videoRef.current && videoRef.current.srcObject) {
        const tracks = videoRef.current.srcObject.getTracks();
        tracks.forEach((track) => {
          track.stop();
          track.enabled = false;
        });
        videoRef.current.srcObject = null;
      }
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [availableCameras, configLoading]);

  const handleCameraToggle = async () => {
    if (availableCameras.length <= 1 || isChangingCamera) return;

    setIsChangingCamera(true);
    setLoading(true);
    setMostrarComponentes(false);

    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
    }

    try {
      const nextIndex = (currentCameraIndex + 1) % availableCameras.length;
      const nextCamera = availableCameras[nextIndex];

      if (videoRef.current && videoRef.current.srcObject) {
        const tracks = videoRef.current.srcObject.getTracks();
        tracks.forEach((track) => {
          track.stop();
          track.enabled = false;
        });
        videoRef.current.srcObject = null;
      }

      await iniciarCamara(nextCamera.deviceId);
      setCurrentCameraIndex(nextIndex);
    } catch (error) {
    } finally {
      setTimeout(() => {
        setIsChangingCamera(false);
        setMostrarComponentes(true);
      }, 600);
    }
  };

  const capturarFrame = async () => {
    if (!videoRef.current || !canvasRef.current) return null;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (video.readyState !== video.HAVE_ENOUGH_DATA) return null;

    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;

    if (!videoWidth || !videoHeight) return null;

    const aspectRatio = videoWidth / videoHeight;
    let newWidth = videoWidth;
    let newHeight = videoHeight;

    if (videoWidth > MAX_WIDTH) {
      newWidth = MAX_WIDTH;
      newHeight = Math.floor(MAX_WIDTH / aspectRatio);
    }

    canvas.width = newWidth;
    canvas.height = newHeight;

    const ctx = canvas.getContext("2d", { willReadFrequently: true });

    // Si el video está en modo espejo, voltear horizontalmente para captura correcta
    if (isMirrored) {
      ctx.save();
      ctx.scale(-1, 1);
      ctx.drawImage(video, -newWidth, 0, newWidth, newHeight);
      ctx.restore();
    } else {
      ctx.drawImage(video, 0, 0, newWidth, newHeight);
    }

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, "image/webp", CALIDAD_WEBP);
    });
  };

  const handleVideoTap = useCallback(async (event) => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    const rect = video.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    setFocusPosition({ x, y });
    setShowFocusIndicator(true);

    if (platform === "ios") {
      try {
        await forceFocus(videoRef);
        await optimizeImageQuality(videoRef);
      } catch (e) {}
    } else {
      try {
        const stream = video.srcObject;
        if (stream) {
          const track = stream.getVideoTracks()[0];
          const capabilities = track.getCapabilities ? track.getCapabilities() : {};

          if (capabilities.focusMode && capabilities.focusMode.includes("manual")) {
            await track.applyConstraints({
              advanced: [{ focusMode: "manual" }]
            });

            setTimeout(async () => {
              await track.applyConstraints({
                advanced: [{ focusMode: "continuous" }]
              });
            }, 1000);
          }
        }
      } catch (e) {}
    }

    setTimeout(() => {
      setShowFocusIndicator(false);
    }, 1500);
  }, [platform]);

  const toggleTorch = useCallback(async () => {
    if (!torchSupported.current || !videoRef.current) return;

    try {
      const stream = videoRef.current.srcObject;
      if (stream) {
        const track = stream.getVideoTracks()[0];
        await track.applyConstraints({
          advanced: [{ torch: !torchEnabled }]
        });
        setTorchEnabled(!torchEnabled);
      }
    } catch (e) {}
  }, [torchEnabled]);

  const analizarFrame = () => {
    if (!videoRef.current || !canvasRef.current || procesandoEnvio.current) {
      if (!procesandoEnvio.current) {
        animationFrameId.current = requestAnimationFrame(analizarFrame);
      }
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (video.readyState !== video.HAVE_ENOUGH_DATA) {
      animationFrameId.current = requestAnimationFrame(analizarFrame);
      return;
    }

    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;

    if (!videoWidth || !videoHeight) {
      animationFrameId.current = requestAnimationFrame(analizarFrame);
      return;
    }

    canvas.width = videoWidth;
    canvas.height = videoHeight;

    const ctx = canvas.getContext("2d", { willReadFrequently: true });

    // Si el video está en modo espejo, voltear horizontalmente el canvas para análisis correcto
    if (isMirrored) {
      ctx.save();
      ctx.scale(-1, 1);
      ctx.drawImage(video, -videoWidth, 0, videoWidth, videoHeight);
      ctx.restore();
    } else {
      ctx.drawImage(video, 0, 0, videoWidth, videoHeight);
    }

    const zonaConfort = getZonaConfort();
    const estado = clasificarDistancia(ctx, videoWidth, videoHeight, zonaConfort);

    setEstadoActual(estado);
    setMensaje(MENSAJES[estado]);

    if (debugMode) {
      const zona = obtenerZona(ctx, videoWidth, videoHeight, zonaConfort);
      const lum = luminanciaPromedio(zona);
      const ocup = ocupacion(zona, lum, UMBRALES.toleranciaLuminancia);
      const bordes = detectarBordes(zona);
      const esquinas = detectarEsquinas(zona);
      const centroDoc = calcularCentroDocumento(esquinas);
      const rotacion = calcularRotacion(esquinas);
      const descentrado = estaDescentrado(centroDoc, zona.width, zona.height);

      setDebugInfo({
        lum: lum.toFixed(2),
        ocup: (ocup * 100).toFixed(2),
        bordes: (bordes * 100).toFixed(2),
        esquinas: esquinas ? esquinas.length : 0,
        rotacion: rotacion.toFixed(2),
        descentrado: descentrado,
        centroX: centroDoc ? centroDoc.x.toFixed(0) : 0,
        centroY: centroDoc ? centroDoc.y.toFixed(0) : 0
      });
    }

    if (estado === "ok" && !procesandoEnvio.current) {
      const ahora = Date.now();
      if (ahora - ultimoEnvio.current >= THROTTLE_ENVIO) {
        ultimoEnvio.current = ahora;
        capturarYGuardarFrame();
      }
    }

    animationFrameId.current = requestAnimationFrame(analizarFrame);
  };

  const capturarYGuardarFrame = async () => {
    const blob = await capturarFrame();
    if (blob) {
      setFramesBuenos((prev) => {
        const nuevos = [...prev, blob];
        if (nuevos.length >= FRAMES_NECESARIOS) {
          enviarFrames(nuevos);
          return [];
        }
        return nuevos;
      });
    }
  };

  const enviarFrames = async (frames) => {
    if (procesandoEnvio.current) return;

    procesandoEnvio.current = true;
    setLoading(true);
    setMostrarComponentes(false);

    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
    }

    try {
      const response = await documentValidator({
        base64Data: null,
        urlService: configValue.urlService,
        isFront,
        documentType,
        processId,
        config: {
          ...configValue,
          documentValidator: {
            ...configValue.documentValidator,
            enable_batch: "true"
          }
        },
        batchImages: frames
      });

      if (response.data.quality && response.status === 200) {
        const finalImage = response.data.images?.original || "";
        responseToHost(isFront === "true" ? 1 : 2, finalImage);
      } else {
        setLoading(false);
        procesandoEnvio.current = false;
        setFramesBuenos([]);
        setMostrarComponentes(true);
        iniciarAnalisis();
        alert("Error al validar el documento. Intenta nuevamente.");
      }
    } catch (error) {
      setLoading(false);
      procesandoEnvio.current = false;
      setFramesBuenos([]);
      setMostrarComponentes(true);
      iniciarAnalisis();
      alert("Error en el envío: " + error.message);
    }
  };

  const responseToHost = (type, image) => {
    const message = {
      status: "success",
      payload: {
        type,
        processId,
        image,
        stepHistory: [],
      },
    };
    window.parent.postMessage(message, "*");
  };

  const iniciarAnalisis = () => {
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
    }
    animationFrameId.current = requestAnimationFrame(analizarFrame);
  };

  const goBack = () => {
    const message = {
      status: "cancel",
      payload: {
        processId,
      },
    };
    window.parent.postMessage(message, "*");
  };

  const getEstadoColor = () => {
    switch (estadoActual) {
      case "ok":
        return "#ACFFB7";
      case "empty":
        return "#E8E8E8";
      case "far":
      case "near":
        return "#FFE066";
      default:
        return "#E8E8E8";
    }
  };

  const zonaConfort = getZonaConfort();

  return (
    <div
      ref={containerAdo}
      className={`container v6 movil ${
        projectName.toLowerCase().includes("lulo") ? "background-lulo" : "background-default"
      }`}
    >
      {loading && (
        <div className="loader-container">
          {projectName.toLowerCase().includes("lulo") ? (
            <LoaderLulo />
          ) : (
            <Loader color="#FFF" primary="#FF3D00" />
          )}
        </div>
      )}

      {mostrarComponentes && (
        <div className="controles-superiores">
          {configValue?.closeIcon?.show !== false && (
            <button onClick={goBack} className="boton-cerrar" style={{ opacity: loading ? 0 : 1 }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="white"/>
              </svg>
            </button>
          )}

          <div className="controles-derecha">
            {torchSupported.current && (
              <button onClick={toggleTorch} className={`boton-linterna ${torchEnabled ? "activo" : ""}`}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 2v6h.01L6 8.01 10 12l-4 4 .01.01H6V22h12v-5.99h-.01L18 16l-4-4 4-3.99-.01-.01H18V2H6zm10 14.5V20H8v-3.5l4-4 4 4zm-4-5l-4-4V4h8v3.5l-4 4z" fill="white"/>
                </svg>
              </button>
            )}

            {availableCameras.length > 1 && configValue?.cameraToggle?.show !== false && (
              <button className="boton-cambiar-camara" onClick={handleCameraToggle} disabled={isChangingCamera}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 4h-3.17L15 2H9L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-8 13c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" fill="white"/>
                  <circle cx="12" cy="12" r="3" fill="white"/>
                  <path d="M14.5 6.5l3-3m0 0l-3-3m3 3H14" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      <div className="camara-contenedor">
        <video
          ref={videoRef}
          className={`video-camara ${isMirrored ? "mirrored" : ""}`}
          autoPlay
          playsInline
          muted
          onClick={handleVideoTap}
        />

        {mostrarComponentes && showFocusIndicator && (
          <div
            className="indicador-enfoque"
            style={{
              left: `${focusPosition.x}px`,
              top: `${focusPosition.y}px`,
            }}
          />
        )}

        {mostrarComponentes && (
          <div
            className="zona-confort-overlay"
            style={{
              border: `4px solid ${getEstadoColor()}`,
              left: `${zonaConfort.x * 100}%`,
              top: `${zonaConfort.y * 100}%`,
              width: `${zonaConfort.width * 100}%`,
              height: `${zonaConfort.height * 100}%`,
            }}
          >
            <div className="esquinas-guia">
              <div className="esquina esquina-tl"></div>
              <div className="esquina esquina-tr"></div>
              <div className="esquina esquina-bl"></div>
              <div className="esquina esquina-br"></div>
            </div>
          </div>
        )}

        {mostrarComponentes && (
          <div
            className="texto-guia-interno"
            style={{
              left: `${zonaConfort.x * 100}%`,
              top: `${zonaConfort.y * 100}%`,
              width: `${zonaConfort.width * 100}%`,
              height: `${zonaConfort.height * 100}%`,
            }}
          >
            <div className="mensaje-contenedor-interno">
              <div className="mensaje-texto-vertical">{mensaje}</div>
              {estadoActual === "ok" && (
                <div className="icono-check">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="white"/>
                  </svg>
                </div>
              )}
            </div>
          </div>
        )}

        {debugMode && mostrarComponentes && (
          <div className="panel-debug">
            <div className="debug-titulo">Debug Info - Análisis Científico</div>
            <div className="debug-seccion">CÁMARA</div>
            <div className="debug-linea">Modo Espejo: {isMirrored ? "Sí (Front Camera)" : "No (Rear Camera)"}</div>
            <div className="debug-linea">Dispositivo: {isMovilDevice ? "Móvil" : "Desktop"}</div>

            <div className="debug-seccion">ZONA DE CONFORT</div>
            <div className="debug-linea">Zona: x={zonaConfort.x}, y={zonaConfort.y}</div>
            <div className="debug-linea">Tamaño: w={zonaConfort.width}, h={zonaConfort.height}</div>

            <div className="debug-seccion">DETECCIÓN</div>
            <div className="debug-linea">Estado: <span className={`estado-${estadoActual}`}>{estadoActual}</span></div>
            <div className="debug-linea">Luminancia: {debugInfo.lum}</div>
            <div className="debug-linea">Ocupación: {debugInfo.ocup}%</div>
            <div className="debug-linea">Bordes: {debugInfo.bordes}%</div>

            <div className="debug-seccion">ESQUINAS Y POSICIÓN</div>
            <div className="debug-linea">Esquinas detectadas: {debugInfo.esquinas}/4</div>
            <div className="debug-linea">Centro Doc: ({debugInfo.centroX}, {debugInfo.centroY})</div>
            <div className="debug-linea">Descentrado: {debugInfo.descentrado ? "Sí ⚠️" : "No ✓"}</div>
            <div className="debug-linea">Rotación: {debugInfo.rotacion}° {parseFloat(debugInfo.rotacion) > 15 ? "⚠️" : "✓"}</div>

            <div className="debug-seccion">CAPTURA</div>
            <div className="debug-linea">Frames: {framesBuenos.length}/{FRAMES_NECESARIOS}</div>
          </div>
        )}
      </div>

      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>
  );
}
